import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { templates, type TeamTemplate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { CreateTemplateModal } from '../components/CreateTemplateModal';
import { timeAgo } from '../lib/utils';

const DEFAULT_BLANK_TEMPLATE: Partial<TeamTemplate> = {
  name: '',
  description: '',
  version: '1.0.0',
  agents: [],
  workflow: [
    { name: 'backlog', description: 'Ideas and feature requests', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'triage', description: 'Analyze feasibility, complexity, risk, and priority', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'planning', description: 'Create detailed implementation plan', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'plan-review', description: 'Waiting for human approval of plan', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'implementation', description: 'Write code and tests', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'testing', description: 'Run automated tests and quality checks', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'review', description: 'Review code quality and completeness', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'deployment', description: 'Deploy changes to staging or production', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'monitoring', description: 'Monitor deployed changes for issues', agents: [], autoAdvance: false, successCriteria: [] },
    { name: 'done', description: 'Completed and merged', agents: [], autoAdvance: false, successCriteria: [] },
  ],
  settings: {
    maxConcurrentRuns: 5,
    autoRetry: true,
    retryCount: 2,
    escalationRules: [],
  },
  isPublic: false,
};

export function TemplatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['templates'],
    queryFn: templates.list,
    enabled: !authLoading && !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (input: { name: string; description?: string; forkFrom?: string }) => {
      if (input.forkFrom) {
        return templates.fork(input.forkFrom, { name: input.name, description: input.description });
      }
      return templates.create({
        ...DEFAULT_BLANK_TEMPLATE,
        name: input.name,
        description: input.description || '',
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast('Template created!', 'success');
      setShowCreate(false);
      if (res.data) navigate(`/templates/${res.data.id}`);
    },
    onError: () => toast('Failed to create template', 'error'),
  });

  const templateList: TeamTemplate[] = data?.data || [];

  return (
    <div className="mx-auto max-w-5xl p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Templates</h1>
          <p className="mt-1 text-sm text-gray-400">Browse and manage AI team templates</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-500"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Template
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-400">
          Failed to load templates. Make sure the API is running.
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && templateList.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-800 py-20">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-800">
            <svg className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-300">No templates yet</h3>
          <p className="mt-1 text-xs text-gray-500">Create your first template to get started</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
          >
            Create Template
          </button>
        </div>
      )}

      {/* Template grid */}
      {templateList.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {templateList.map((template) => (
            <button
              key={template.id}
              onClick={() => navigate(`/templates/${template.id}`)}
              className="group cursor-pointer rounded-xl border border-gray-800 bg-surface-850 p-5 text-left transition-all hover:border-gray-700 hover:bg-surface-800"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-100 group-hover:text-primary-400">
                    {template.name}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-500">{template.description}</p>
                </div>
                {template.createdBy === 'system' ? (
                  <span className="shrink-0 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-400">
                    Provided
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-400">
                    User
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3 text-[10px] text-gray-600">
                <span>v{template.version}</span>
                <span>{template.agents.length} agents</span>
                <span>Updated {timeAgo(template.updatedAt)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateTemplateModal
          onSubmit={(data) => createMutation.mutate(data)}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
